import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, Check, Building2 } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SelectField } from '@/components/ui/SelectField';

interface Plan {
  id: string; name: string; price: number;
  features: string[]; limits: Record<string, any>; createdAt: string;
  orgCount: number;
  moduleIds?: string[];
  moduleNames?: string[];
}

interface PlanModule {
  id: string;
  name: string;
  type: 'M' | 'S';
  parentId: string | null;
  orderNumber: number;
}

const EMPTY_FORM = {
  name: '', price: '', leadLimit: '', userLimit: '', trialDays: '0',
  isActive: true,
  moduleIds: [] as string[],
  features: [] as string[],   // free-form marketing bullets shown on plan card
};

// ── Tier presets — one click to fill in modules + features + limits ──────────
// Module identifiers are catalog NAMES (resolved to ids at apply time using
// the live AppModule catalog). Sub-page names are also listed; if a name is
// missing from the catalog the apply step just skips it.
interface PresetSpec {
  name: string;
  price: number;
  trialDays: number;
  leadLimit: number | null;
  userLimit: number | null;
  modules: string[];   // mains + subs by AppModule.name
  features: string[];  // marketing bullets
}

// Master list of feature bullets surfaced in the dropdown. Built from
// the preset specs below — every recommended phrase shows up once,
// deduplicated and grouped so the admin almost never has to type.
function buildFeatureCatalog(): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const tier of ['free', 'starter', 'growth'] as const) {
    for (const f of TIER_PRESETS[tier].features) {
      const key = f.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        ordered.push(f);
      }
    }
  }
  return ordered;
}

const TIER_PRESETS: Record<'free' | 'starter' | 'growth', PresetSpec> = {
  free: {
    name: 'Free',
    price: 0,
    trialDays: 0,
    leadLimit: 100,
    userLimit: 3,
    modules: [
      'Dashboard',
      'Leads', 'All Leads',
      'Tasks',
      'Reminders',
      'Settings',
      'Support',
    ],
    // Leads / team members come from Lead Limit + User Limit boxes,
    // do not duplicate them here.
    features: [
      'Manual lead capture',
      'Tasks & Reminders',
      'Basic dashboard',
      'Email support',
    ],
  },
  starter: {
    name: 'Starter',
    price: 29,
    trialDays: 0,
    leadLimit: 2000,
    userLimit: 10,
    modules: [
      'Dashboard',
      'Leads', 'All Leads', 'Lead Assign', 'Trash',
      'Tasks',
      'Reminders',
      'Time Availability',
      'Email Templates',
      'Reports',
      'Administrator', 'Roles', 'Users',
      'Settings',
      'Support',
    ],
    features: [
      'Auto lead assignment (round-robin)',
      'Email templates + sequences (up to 3)',
      'CSV import / export',
      'Lead deduplication',
      'Custom fields + saved filters',
      'Public booking page',
      'Trash + restore (30 days)',
      'Roles + permissions',
      'Basic reports',
      'Priority email support',
    ],
  },
  growth: {
    name: 'Growth',
    price: 79,
    trialDays: 0,
    leadLimit: 10000,
    userLimit: null, // unlimited
    modules: [
      'Dashboard',
      'Leads', 'All Leads', 'Lead Assign', 'Trash',
      'Tasks',
      'Reminders',
      'Time Availability',
      'Campaigns',
      'WhatsApp Chat',
      'Email Templates',
      'Reports',
      'Billing',
      'Administrator', 'Roles', 'Users', 'Teams', 'Audit Log', 'Developer',
      'Settings',
      'Support',
    ],
    features: [
      'Fetch leads from Facebook Lead Ads',
      'Google Ads lead form webhook',
      'WhatsApp Cloud API chat + chatbot',
      'AI Assistant (Ask BrixLead — Claude)',
      'AI predictions, smart alerts, draft messages',
      'Lead scoring engine (Hot / Warm / Cold)',
      'Advanced auto-assignment (geo, caps, working hours)',
      'Marketing campaigns (Email + WhatsApp + SMS)',
      'Unlimited email sequences',
      'Advanced reports — Funnel, Team, Campaign ROI',
      'Google Calendar + Meet + Zoom auto-links',
      'Public REST API + Webhooks',
      'Audit log',
      'White-label branding',
      'Teams + reporting manager hierarchy',
      'GDPR / DPDP compliance',
      'Phone + priority support',
    ],
  },
};

// ── Module tree (parent + children with cascade selection) ─────────────────────
function ModuleTree({
  catalog, selected, onToggle, onToggleParent,
}: {
  catalog: PlanModule[];
  selected: string[];
  onToggle: (id: string) => void;
  onToggleParent: (parentId: string, childIds: string[], shouldCheck: boolean) => void;
}) {
  const mains = catalog.filter(m => m.type === 'M').sort((a, b) => a.orderNumber - b.orderNumber);
  const subsByParent = new Map<string, PlanModule[]>();
  for (const s of catalog.filter(m => m.type === 'S')) {
    if (!s.parentId) continue;
    const arr = subsByParent.get(s.parentId) ?? [];
    arr.push(s);
    subsByParent.set(s.parentId, arr);
  }
  for (const arr of subsByParent.values()) arr.sort((a, b) => a.orderNumber - b.orderNumber);
  const sel = new Set(selected);

  return (
    <div className="space-y-2 border border-gray-100 rounded-lg p-2 bg-slate-50/30">
      {mains.map(main => {
        const subs = subsByParent.get(main.id) ?? [];
        const childIds = subs.map(s => s.id);
        const allChildrenChecked = childIds.length > 0 && childIds.every(id => sel.has(id));
        const someChildrenChecked = childIds.some(id => sel.has(id));
        const parentChecked = sel.has(main.id);
        // "Effectively checked" when parent toggle reflects parent + all
        // children. Use indeterminate via aria when partial.
        const effective = parentChecked && (childIds.length === 0 || allChildrenChecked);
        return (
          <div key={main.id} className="rounded-lg bg-white border border-gray-100 overflow-hidden">
            <button
              type="button"
              onClick={() => onToggleParent(main.id, childIds, !parentChecked)}
              className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors ${
                effective ? 'bg-indigo-50/70 text-[#6366F1] font-medium' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                effective
                  ? 'bg-[#6366F1] border-[#6366F1]'
                  : someChildrenChecked || parentChecked
                    ? 'bg-indigo-200 border-indigo-300'
                    : 'border-gray-300'
              }`}>
                {effective && <Check className="h-2.5 w-2.5 text-white" />}
                {!effective && (someChildrenChecked || parentChecked) && (
                  <span className="block w-2 h-0.5 bg-[#6366F1]" />
                )}
              </div>
              {main.name}
              {subs.length > 0 && (
                <span className="ml-auto text-[10px] text-slate-400 font-normal">
                  {subs.filter(s => sel.has(s.id)).length}/{subs.length} sub-pages
                </span>
              )}
            </button>
            {subs.length > 0 && (
              <div className="border-t border-gray-50 px-3 py-2 grid grid-cols-2 gap-1 bg-slate-50/30">
                {subs.map(sub => {
                  const checked = sel.has(sub.id);
                  return (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => onToggle(sub.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-colors ${
                        checked ? 'bg-indigo-50 text-[#6366F1] font-medium' : 'text-slate-500 hover:bg-white'
                      }`}
                    >
                      <div className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center border ${
                        checked ? 'bg-[#6366F1] border-[#6366F1]' : 'border-gray-300'
                      }`}>
                        {checked && <Check className="h-2 w-2 text-white" />}
                      </div>
                      {sub.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Plan Form Drawer ───────────────────────────────────────────────────────────
function PlanFormDrawer({ plan, onClose, onSaved }: { plan?: Plan; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!plan;
  const [form, setForm] = useState(() => {
    if (!plan) return EMPTY_FORM;
    const lim = plan.limits || {};
    return {
      name: plan.name,
      price: String(plan.price),
      leadLimit: lim.leadLimit != null ? String(lim.leadLimit) : '',
      userLimit: lim.userLimit != null ? String(lim.userLimit) : '',
      trialDays: String(lim.trialDays ?? '0'),
      isActive: lim.isActive !== false,
      moduleIds: Array.isArray(plan.moduleIds) ? [...plan.moduleIds] : [],
      features: Array.isArray(plan.features) ? [...plan.features] : [],
    };
  });
  const [featureSelect, setFeatureSelect] = useState('');
  const [featureCustom, setFeatureCustom] = useState('');
  const [showCustomFeature, setShowCustomFeature] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState<PlanModule[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Fetch available tenant-side modules so the checklist mirrors the
  // current AppModule catalog (super-admin adds a module in /modules,
  // it shows up here immediately).
  useEffect(() => {
    saApi.get('/super-admin/plan-modules')
      .then(r => setCatalog(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setCatalogLoading(false));
  }, []);

  const toggleModule = (id: string) =>
    setForm(p => ({
      ...p,
      moduleIds: p.moduleIds.includes(id) ? p.moduleIds.filter(x => x !== id) : [...p.moduleIds, id],
    }));

  const toggleAllModules = () => {
    setForm(p => ({
      ...p,
      moduleIds: p.moduleIds.length === catalog.length ? [] : catalog.map(m => m.id),
    }));
  };

  const addFeatureValue = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    if (form.features.some(f => f.toLowerCase() === v.toLowerCase())) return;
    setForm(p => ({ ...p, features: [...p.features, v] }));
  };
  const addFromDropdown = () => {
    if (!featureSelect) return;
    addFeatureValue(featureSelect);
    setFeatureSelect('');
  };
  const addCustomFeature = () => {
    addFeatureValue(featureCustom);
    setFeatureCustom('');
  };
  const removeFeature = (f: string) =>
    setForm(p => ({ ...p, features: p.features.filter(x => x !== f) }));

  // Master dropdown — only show options not already added.
  const FEATURE_CATALOG = buildFeatureCatalog();
  const availableFeatures = FEATURE_CATALOG.filter(
    f => !form.features.some(added => added.toLowerCase() === f.toLowerCase()),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Plan name is required'); return; }
    if (form.moduleIds.length === 0) { setError('Pick at least one module for this plan'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        price: parseFloat(form.price) || 0,
        // Marketing bullets that show on the plan card — kept separate
        // from moduleIds so module access does not overwrite copy like
        // "AI Lead Scoring" or "Phone Support".
        features: form.features,
        moduleIds: form.moduleIds,
        limits: {
          leadLimit: form.leadLimit ? parseInt(form.leadLimit) : null,
          userLimit: form.userLimit ? parseInt(form.userLimit) : null,
          trialDays: parseInt(form.trialDays) || 0,
          isActive: form.isActive,
        },
        trialDays: parseInt(form.trialDays) || 0,
        isActive: form.isActive,
      };
      if (isEdit) await saApi.patch(`/super-admin/plans/${plan!.id}`, payload);
      else await saApi.post('/super-admin/plans', payload);
      toast('success', isEdit ? 'Plan updated' : 'Plan created');
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save plan');
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-10 pl-3 pr-3 rounded-lg border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-[#94A3B8] transition-all';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full sm:w-[500px] sm:max-w-none flex-shrink-0 bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-[#1E293B]">{isEdit ? 'Edit Plan' : 'New Plan'}</h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#64748B] transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Plan Name <span className="text-red-500">*</span></label>
            <input className={inputCls} placeholder="e.g. Starter, Pro, Enterprise" required
              value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-normal text-[#64748B]">Price / month ($)</label>
            <input type="number" min="0" step="0.01" className={inputCls} placeholder="0 = Free"
              value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="block text-xs font-normal text-[#64748B]">Lead Limit / month</label>
              <input type="number" min="0" className={inputCls} placeholder="Blank = Unlimited"
                value={form.leadLimit} onChange={e => setForm(p => ({ ...p, leadLimit: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-normal text-[#64748B]">User Limit</label>
              <input type="number" min="0" className={inputCls} placeholder="Blank = Unlimited"
                value={form.userLimit} onChange={e => setForm(p => ({ ...p, userLimit: e.target.value }))} />
            </div>
          </div>

          {/* Modules included in this plan — main modules + their sub-pages */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-normal text-[#64748B]">Modules Included</label>
              {catalog.length > 0 && (
                <button type="button" onClick={toggleAllModules}
                  className="text-[11px] text-[#6366F1] hover:underline">
                  {form.moduleIds.length === catalog.length ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
            {catalogLoading ? (
              <div className="rounded-lg border border-gray-100 px-4 py-6 text-center text-xs text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading modules…
              </div>
            ) : catalog.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                No tenant modules in the catalog yet. Add some in the Modules page first.
              </div>
            ) : (
              <ModuleTree
                catalog={catalog}
                selected={form.moduleIds}
                onToggle={toggleModule}
                onToggleParent={(parentId, childIds, shouldCheck) => {
                  setForm(p => {
                    const set = new Set(p.moduleIds);
                    if (shouldCheck) { set.add(parentId); for (const id of childIds) set.add(id); }
                    else { set.delete(parentId); for (const id of childIds) set.delete(id); }
                    return { ...p, moduleIds: [...set] };
                  });
                }}
              />
            )}
            <p className="text-[11px] text-slate-400">
              Tenants on this plan only see these modules in their sidebar. Admins can grant subsets to team members via Roles.
            </p>
          </div>

          {/* Marketing features — pick from preset catalogue (typo-safe) */}
          <div className="space-y-2">
            <label className="block text-xs font-normal text-[#64748B]">Features (marketing bullets)</label>

            {/* Primary: dropdown of every recommended feature, deduped. */}
            <div className="flex gap-2">
              <SelectField
                className={`${inputCls} bg-white flex-1`}
                value={featureSelect}
                onChange={e => setFeatureSelect(e.target.value)}
              >
                <option value="">— Select a feature to add —</option>
                {availableFeatures.length === 0 ? (
                  <option value="" disabled>All preset features already added</option>
                ) : (
                  availableFeatures.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))
                )}
              </SelectField>
              <button type="button" onClick={addFromDropdown}
                disabled={!featureSelect}
                className="h-10 px-3 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
                Add
              </button>
            </div>

            {/* Secondary: custom entry — collapsed by default */}
            {!showCustomFeature ? (
              <button type="button" onClick={() => setShowCustomFeature(true)}
                className="text-[11px] text-[#6366F1] hover:underline">
                + Add custom feature
              </button>
            ) : (
              <div className="flex gap-2 items-start">
                <input
                  className={`${inputCls} flex-1`}
                  placeholder="Type custom feature text"
                  value={featureCustom}
                  onChange={e => setFeatureCustom(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); addCustomFeature(); }
                  }}
                />
                <button type="button" onClick={addCustomFeature}
                  disabled={!featureCustom.trim()}
                  className="h-10 px-3 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
                  Add
                </button>
                <button type="button" onClick={() => { setShowCustomFeature(false); setFeatureCustom(''); }}
                  className="h-10 px-2 text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Selected chips */}
            {form.features.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {form.features.map(f => (
                  <span key={f} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-[#6366F1] text-xs">
                    <Check className="h-3 w-3" />
                    {f}
                    <button type="button" onClick={() => removeFeature(f)}
                      className="ml-0.5 -mr-0.5 text-[#6366F1]/60 hover:text-red-500 transition-colors"
                      aria-label={`Remove ${f}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">
                Pick bullets from the dropdown — they will show on the plan card. Module access is configured above.
              </p>
            )}
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
            <div>
              <p className="text-sm font-medium text-[#1E293B]">Active Plan</p>
              <p className="text-xs text-[#94A3B8]">Visible to customers during signup</p>
            </div>
            <button type="button" onClick={() => setForm(p => ({ ...p, isActive: !p.isActive }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-[#6366F1]' : 'bg-gray-300'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">{error}</div>
          )}
        </form>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-[#64748B] hover:text-[#1E293B] border border-gray-200 hover:border-gray-300 transition-all">
            Cancel
          </button>
          <button onClick={handleSubmit as any} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#6366F1] hover:bg-[#4F46E5] text-white transition-colors disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Plans Page ─────────────────────────────────────────────────────────────────
export function SuperAdminPlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<{ open: boolean; plan?: Plan }>({ open: false });
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmPlan, setConfirmPlan] = useState<Plan | null>(null);
  const [confirmPlanLoading, setConfirmPlanLoading] = useState(false);
  // Annual discount pulled from the SAME payment config the checkout +
  // marketing pages use. Previously the yearly price was computed with a
  // hardcoded 0.85 (15%) here, which contradicted the 20% shown on the
  // pricing page and charged at checkout. Fetch it so the super-admin
  // sees the real annual price.
  const [annualDiscount, setAnnualDiscount] = useState(20);

  const fetchPlans = () => {
    setLoading(true);
    saApi.get('/super-admin/plans')
      .then(r => setPlans(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPlans();
    // Public endpoint — no auth needed, and it's the same value the
    // marketing pricing page reads, guaranteeing they never diverge.
    fetch('/api/public/billing')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const pct = Number(d?.annualDiscountPercent);
        if (Number.isFinite(pct)) setAnnualDiscount(pct);
      })
      .catch(() => { /* keep 20% default */ });
  }, []);

  const handleDelete = (plan: Plan) => {
    if (plan.orgCount > 0) {
      toast('error', `Cannot delete "${plan.name}" — ${plan.orgCount} organization(s) are using it.`);
      return;
    }
    setConfirmPlan(plan);
  };

  const handleConfirmDelete = async () => {
    if (!confirmPlan) return;
    setConfirmPlanLoading(true);
    setDeleting(confirmPlan.id);
    try {
      await saApi.delete(`/super-admin/plans/${confirmPlan.id}`);
      setConfirmPlan(null);
      fetchPlans();
      toast('success', 'Plan deleted');
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to delete plan');
    } finally { setDeleting(null); setConfirmPlanLoading(false); }
  };

  return (
    <SuperAdminLayout title="Plans">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Plan</h1>
            <p className="text-sm text-slate-500">Manage and review subscription plans</p>
          </div>
          <button onClick={() => setDrawer({ open: true })}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors">
            <Plus className="h-4 w-4" /> New Plan
          </button>
        </div>

        {/* Plan cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
        ) : plans.length === 0 ? (
          <div className="text-center py-20 text-slate-400 text-sm">No plans yet. Create your first plan.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map(plan => {
              const lim = plan.limits || {};
              const isActive = lim.isActive !== false;
              const isFree = plan.price === 0;
              const features = Array.isArray(plan.features) ? plan.features : [];
              const moduleNames = Array.isArray(plan.moduleNames) ? plan.moduleNames : [];
              const yearlyPrice = (plan.price * 12 * (1 - annualDiscount / 100)).toFixed(2);

              return (
                <div key={plan.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                  {/* Actions */}
                  <div className="flex justify-end gap-1 px-4 pt-4">
                    <button onClick={() => setDrawer({ open: true, plan })}
                      className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(plan)}
                      disabled={deleting === plan.id || plan.orgCount > 0}
                      title={plan.orgCount > 0 ? `${plan.orgCount} org(s) using this plan` : 'Delete plan'}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40">
                      {deleting === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Plan name + badge */}
                  <div className="text-center px-6 pb-4">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${
                        isFree ? 'bg-slate-50 text-slate-500 border-slate-200' : 'bg-indigo-50 text-indigo-600 border-indigo-200'
                      }`}>
                        {isFree ? 'Free' : 'Paid'}
                      </span>
                    </div>

                    {/* Price */}
                    <div className="mb-1">
                      <span className="text-3xl font-extrabold text-slate-900">${plan.price.toFixed(2)}</span>
                      <span className="text-sm text-slate-400 font-medium">/MONTH</span>
                    </div>
                    <p className="text-xs text-slate-400">${yearlyPrice}/Year</p>

                    {/* Orgs using */}
                    {plan.orgCount > 0 && (
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600">
                        <Building2 className="h-3 w-3" />{plan.orgCount} org{plan.orgCount > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  {/* Limits */}
                  <div className="flex-1 border-t border-slate-100">
                    <div className="divide-y divide-slate-100">
                      {lim.leadLimit != null && (
                        <div className="px-6 py-3 text-center">
                          <span className="font-bold text-slate-900">{lim.leadLimit.toLocaleString()}</span>
                          <span className="text-sm text-slate-500 ml-1">Leads</span>
                        </div>
                      )}
                      {lim.userLimit != null && (
                        <div className="px-6 py-3 text-center">
                          <span className="font-bold text-slate-900">{lim.userLimit}</span>
                          <span className="text-sm text-slate-500 ml-1">Team Members</span>
                        </div>
                      )}
                    </div>

                    {/* Marketing features — shown first as the customer-facing pitch */}
                    {features.length > 0 && (
                      <div className="border-t border-slate-100">
                        <p className="px-6 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Features</p>
                        <div className="divide-y divide-slate-100">
                          {features.map((f: string) => (
                            <div key={f} className="px-6 py-2 flex items-center gap-2 text-sm text-slate-600">
                              <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                              {f}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Modules included (access) — what the tenant can see in their sidebar */}
                    {moduleNames.length > 0 && (
                      <div className="border-t border-slate-100">
                        <p className="px-6 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Modules</p>
                        <div className="divide-y divide-slate-100">
                          {moduleNames.map((m: string) => (
                            <div key={m} className="px-6 py-2 flex items-center gap-2 text-sm text-slate-700">
                              <Check className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                              {m}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Status */}
                    <div className="px-6 py-3 border-t border-slate-100 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {drawer.open && (
        <PlanFormDrawer
          plan={drawer.plan}
          onClose={() => setDrawer({ open: false })}
          onSaved={() => { setDrawer({ open: false }); fetchPlans(); }}
        />
      )}
      <ConfirmModal
        isOpen={!!confirmPlan}
        title="Delete Plan?"
        message={`Delete plan "${confirmPlan?.name}"? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmPlan(null)}
        isLoading={confirmPlanLoading}
      />
    </SuperAdminLayout>
  );
}
